import _ from 'lodash';
import { SCHEDULE_PROTOCOL_VERSION } from '../Constants/constants';
import { normalizeScheduleMetadata, transformScheduleValuesForSend } from './scheduling_utils';

// Simulation compares editable recipe values, not revision/runtime telemetry.
// A time string or a custom curve is one setting; indexed rows retain identity.
function changedSettings(next, previous, key) {
  if (_.isEqual(next, previous)) return 0;
  if (key === 'points' || next === null || typeof next !== 'object') return 1;
  if (Array.isArray(next)) {
    return Number(next.length !== previous?.length) + next.reduce((count, value, index) => {
      const identity = value?.phase_id !== undefined ? 'phase_id' : value?.tank_id !== undefined ? 'tank_id' : null;
      const old = identity ? previous?.find((entry) => entry[identity] === value[identity]) : previous?.[index];
      return count + changedSettings(value, old);
    }, 0);
  }
  return Object.entries(next).reduce((count, [field, value]) =>
    count + (['phase_id', 'protocol_version', 'recipe_revision', 'base_recipe_revision'].includes(field)
      ? 0 : changedSettings(value, previous?.[field], field)), 0);
}

export function retainSimulationScheduling(simulation, response) {
  simulation.scheduleScopes ??= {};
  // Runtime lighting consumes the same committed recipe/readback owner.
  simulation.model.scheduleScopes = simulation.scheduleScopes;
  const scopes = response.is_zoned
    ? response.data.zones.map((zone) => [Number(zone.id), zone.settings])
    : [[0, response.data]];
  for (const [id, settings] of scopes) {
    if (simulation.scheduleScopes[id])
      Object.assign(settings, _.cloneDeep(simulation.scheduleScopes[id]));
    else if (settings.schedule_meta && settings.schedule_values) {
      simulation.scheduleScopes[id] = _.cloneDeep({
        schedule_meta: settings.schedule_meta,
        schedule_values: settings.schedule_values
      });
    }
  }
}

export function saveSimulationSchedule(simulation, data) {
  const zone = simulation.zones.length ? Number(data.id ?? data.zone) : 0;
  const current = simulation.scheduleScopes?.[zone];
  const meta = data.schedule_meta;
  const recipe = data.schedule_values;
  const base = Number(recipe?.base_recipe_revision);
  const fail = () => ({
    type: 'error',
    dont_log: true,
    timestamp: Date.now(),
    message_parts: ['schedule_upload_failed', String(zone), String(base), 'controller_rejected']
  });
  if (
    !current ||
    meta?.protocol_version !== SCHEDULE_PROTOCOL_VERSION ||
    recipe?.protocol_version !== SCHEDULE_PROTOCOL_VERSION ||
    base !== current.schedule_meta.recipe_revision ||
    !Array.isArray(recipe.scheduling_values) ||
    !recipe.scheduling_values.length ||
    recipe.scheduling_values.length !== recipe.schedule_values_count ||
    meta.active_phase_cnt !== recipe.schedule_values_count
  )
    return fail();
  const revision = (base + 1) >>> 0 || 1;
  const previousPhases = transformScheduleValuesForSend(current.schedule_values).scheduling_values;
  const accepted = changedSettings(normalizeScheduleMetadata(meta), normalizeScheduleMetadata(current.schedule_meta)) +
    // The metadata phase count already counts an added/removed phase.
    recipe.scheduling_values.reduce((count, phase) => count + changedSettings(phase,
      previousPhases.find(
        (saved) => saved.phase_id === phase.phase_id)), 0);
  simulation.scheduleScopes[zone] = _.cloneDeep({
    schedule_meta: { ...meta, recipe_revision: revision },
    schedule_values: {
      ...recipe,
      recipe_revision: revision,
      base_recipe_revision: revision,
      active_recipe_revision: revision,
      runtime_status: meta.schedule_enabled ? 1 : 0,
      active_phase_index: meta.schedule_enabled ? 0 : 255,
      recipe_valid: true
    }
  });
  return {
    type: 'info',
    dont_log: true,
    timestamp: Date.now(),
    message_parts: accepted
      ? ['scheduling', `: ${accepted} `, accepted === 1 ? 'gc_ms_setting_accepted' : 'gc_ms_settings_accepted']
      : ['scheduling', ': ', 'gc_ms_nothing_changed']
  };
}
